
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { OrderWithCustomer } from "../types/orderTypes";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Edit2, Trash2, MoreHorizontal, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

interface OrdersTableProps {
  orders: OrderWithCustomer[];
  selectedOrders: string[];
  toggleOrderSelection: (id: string) => void;
  toggleSelectAll: () => void;
  areAllSelected: boolean;
  handleEditOrder: (id: string) => void;
  handleDeleteOrder: (id: string) => void;
  formatDate: (date: string) => string;
  formatStatus: (status: string) => { label: string; className: string };
  formatPaymentStatus: (status: string) => { label: string; className: string };
}

const OrdersTable = ({
  orders,
  selectedOrders,
  toggleOrderSelection,
  toggleSelectAll,
  areAllSelected,
  handleEditOrder,
  handleDeleteOrder,
  formatDate,
  formatStatus,
  formatPaymentStatus
}: OrdersTableProps) => {
  return (
    <div className="reference-table-container">
      <Table>
        <TableHeader className="reference-table-header">
          <TableRow>
            <TableHead className="w-[40px] reference-table-cell-header">
              <Checkbox 
                checked={areAllSelected}
                onCheckedChange={toggleSelectAll}
                aria-label="Sélectionner toutes les commandes"
              />
            </TableHead>
            <TableHead className="w-[140px] reference-table-cell-header">Numéro</TableHead>
            <TableHead className="w-[180px] reference-table-cell-header">Date</TableHead>
            <TableHead className="reference-table-cell-header">Client</TableHead>
            <TableHead className="w-[130px] reference-table-cell-header">Statut</TableHead>
            <TableHead className="w-[130px] reference-table-cell-header">Paiement</TableHead>
            <TableHead className="text-right w-[130px] reference-table-cell-header">Montant</TableHead>
            <TableHead className="w-[80px] text-right reference-table-cell-header">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="h-24 text-center">
                <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                  <AlertTriangle className="h-8 w-8 mb-2" />
                  <p>Aucune commande trouvée</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            orders.map((order) => {
              const statusFormatted = formatStatus(order.status);
              const paymentStatusFormatted = formatPaymentStatus(order.payment_status);
              
              return (
                <TableRow 
                  key={order.id} 
                  className={selectedOrders.includes(order.id) 
                    ? 'reference-table-row-selected' 
                    : 'reference-table-row'
                  }
                >
                  <TableCell className="reference-table-cell">
                    <Checkbox 
                      checked={selectedOrders.includes(order.id)}
                      onCheckedChange={() => toggleOrderSelection(order.id)}
                      aria-label={`Sélectionner la commande ${order.order_number || order.id}`}
                    />
                  </TableCell>
                  <TableCell className="reference-table-cell font-medium text-gray-900 dark:text-gray-200">
                    {order.order_number || `CMD-${order.id.slice(0, 8)}`}
                  </TableCell>
                  <TableCell className="reference-table-cell">
                    {formatDate(order.order_date)}
                  </TableCell>
                  <TableCell className="reference-table-cell">
                    {order.customer_name}
                  </TableCell>
                  <TableCell className="reference-table-cell">
                    <Badge variant="outline" className={statusFormatted.className}>
                      {statusFormatted.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="reference-table-cell">
                    <Badge variant="outline" className={paymentStatusFormatted.className}>
                      {paymentStatusFormatted.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="reference-table-cell text-right font-medium text-gray-900 dark:text-gray-200">
                    {new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(order.total_amount)}
                  </TableCell>
                  <TableCell className="reference-table-cell">
                    <div className="flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 dark:text-gray-300 dark:hover:bg-gray-800"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="data-table-dropdown">
                          <DropdownMenuLabel className="dark:text-gray-300">Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator className="data-table-dropdown-separator" />
                          <DropdownMenuItem 
                            onClick={() => handleEditOrder(order.id)}
                            className="data-table-dropdown-item"
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600 dark:text-red-400 data-table-dropdown-item"
                            onClick={() => handleDeleteOrder(order.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default OrdersTable;
